import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { createBrowserRouter, Navigate, useLocation, useParams, useRouteError, useSearchParams } from "react-router";
import { RETIRED_ROUTE_REDIRECTS } from "./retiredRouteRedirects";
import { captureException } from "../lib/sentryLazy";
import { Home, RotateCcw, Shield } from "lucide-react";
import { useAuthContext } from "../contexts/AuthContext";
import { LazyCountyConfigProvider } from "../lib/cie/LazyCountyConfigProvider";
import { getPropertyLookupKey, hasLegacyPropertyCheckContext, hasPropertyLookupState } from "../lib/propertyCheckNavigation";
import { usePropertyCheckContext } from "./hooks/usePropertyCheckContext";
import { LegacyPropertyCheckRedirect } from "./components/LegacyPropertyCheckRedirect";
import { PropertyCheckNavigation } from "./components/PropertyCheckNavigation";
import { resolveManualEntryRouteContext } from "./components/manualEntryRouteContext";
import { useNoIndexMeta } from "../hooks/usePageMeta";

/** Preserve legacy /property/:qrCode links by redirecting them to /a/:code. */
function PropertyQrRedirect() {
  const { qrCode } = useParams();
  return <Navigate to={`/a/${qrCode}`} replace />;
}

/**
 * UF-04: /dashboard is no longer the primary analysis surface. The wizard
 * now renders the full PropertyAnalysisPanel inline. This gate preserves
 * backward compatibility for existing external links (postcards, emails,
 * blog posts) while steering traffic to the new unified flow.
 *
 * Routing rules:
 *   /dashboard?address=...       → analysis result page
 *   /dashboard?payment=success   → /appeal-hq?payment=success (Stripe legacy)
 *   /dashboard (auth'd)          → /appeal-hq (returning-user hub)
 *   /dashboard (unauth)          → / (homepage)
 */
function DashboardGate() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const context = usePropertyCheckContext();
  const { isAuthenticated, isLoading } = useAuthContext();

  const payment = searchParams.get("payment");
  const qs = searchParams.toString();

  if (hasLegacyPropertyCheckContext(location.pathname, location.search)) {
    return <LegacyPropertyCheckRedirect />;
  }

  // Context present → render the clean analysis result page. The result-page
  // CTA remains the deliberate transition into the filing-document wizard.
  if (context) {
    return (
      <PropertyCheckNavigation>
        <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><span className="text-fp-slate-400 text-base">Loading...</span></div>}>
          <PropertyAnalysisDashboard key={getPropertyLookupKey(location.state)} />
        </Suspense>
      </PropertyCheckNavigation>
    );
  }

  if (hasPropertyLookupState(location.state)) return <PropertyLookup />;

  // Legacy Stripe redirect → new home for post-purchase confirmation
  if (payment) {
    return <Navigate to={`/appeal-hq${qs ? `?${qs}` : ""}`} replace />;
  }

  // Wait for auth resolution before deciding where to send the user
  if (isLoading) return null;

  return isAuthenticated
    ? <Navigate to="/appeal-hq" replace />
    : <Navigate to="/" replace />;
}

/**
 * /check recovers only the lookup named by its history entry. Data
 * availability changes the explanation and next action, never the visual
 * generation. Already-issued property-code routes keep their dedicated flow.
 */
function CheckGate() {
  // Property-specific workflow state is not a public search landing page.
  useNoIndexMeta();
  const location = useLocation();
  const context = usePropertyCheckContext();

  if (hasLegacyPropertyCheckContext(location.pathname, location.search)) {
    return <LegacyPropertyCheckRedirect />;
  }
  if (context) {
    return (
      <PropertyCheckNavigation>
        <DecisionDeciderWrapper key={getPropertyLookupKey(location.state)} />
      </PropertyCheckNavigation>
    );
  }

  // Keep the clean route and attribution. Never substitute a recent search
  // when this history entry has no usable context (including blocked storage).
  return <PropertyLookup />;
}

function ManualEntryRoute() {
  const [searchParams] = useSearchParams();
  const routeContext = resolveManualEntryRouteContext(searchParams);

  // Rendered inside the route's <Suspense>; the provider chunk (and the CIE
  // registry it carries) loads with the manual-entry path, not with the entry.
  return (
    <LazyCountyConfigProvider
      countyId={routeContext.countyId}
      countyName={routeContext.countyName}
      stateAbbr={routeContext.stateAbbr}
    >
      <ManualEntryPath />
    </LazyCountyConfigProvider>
  );
}

// ---------------------------------------------------------------------------
// Chunk-load error detection (shared by RootErrorElement & WizardErrorElement)
// ---------------------------------------------------------------------------

const CHUNK_RELOAD_KEY = "fp_chunk_reload";

const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Loading chunk",
  "Loading CSS chunk",
  "ChunkLoadError",
  "is not a valid JavaScript MIME type",
  "Importing a module script failed",
  "error loading dynamically imported module",
] as const;

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p.toLowerCase()));
  }
  if (
    error &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "ChunkLoadError"
  ) {
    return true;
  }
  return false;
}

/** Auto-reload once for stale chunk errors; return true if reloading. */
function tryChunkReload(): boolean {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
    return true;
  }
  return false;
}

/** Production-safe root error element — replaces React Router's dev page. */
function RootErrorElement() {
  const error = useRouteError();

  // Stale-deploy chunk error → silent auto-reload (one attempt)
  if (isChunkLoadError(error)) {
    if (tryChunkReload()) {
      // Returning empty div while reload kicks in
      return <div />;
    }
    // Already reloaded once — show friendly "new version" UI.
    // Log at info level: expected post-deploy noise, not an actionable bug.
    captureException(error, (scope) => {
      scope.setLevel("info");
      scope.setTag("error.type", "chunk_load");
      scope.setTag("chunk.reload_attempted", "true");
    });

    return (
      <div className="min-h-screen bg-fp-slate-100 font-inter flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-lg w-full text-center">
          <div className="w-14 h-14 rounded-full bg-fp-amber-100 flex items-center justify-center mx-auto mb-4">
            <RotateCcw className="w-7 h-7 text-fp-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-fp-navy-900 mb-2">
            New Version Available
          </h1>
          <p className="text-slate-500 text-sm mb-6">
            FairPath was just updated. Please reload to get the latest version.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                window.location.reload();
              }}
              className="inline-flex items-center gap-2 bg-fp-emerald-500 hover:bg-fp-emerald-600 text-white font-bold py-3 px-6 rounded-lg shadow-sm transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reload Page
            </button>
            <a
              href="/"
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-fp-navy-900 font-medium py-3 px-6 rounded-lg border border-slate-200 transition-colors"
            >
              <Home className="w-4 h-4" />
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Non-chunk error — report to Sentry and show generic error
  captureException(error);

  return (
    <div className="min-h-screen bg-fp-slate-100 font-inter flex items-center justify-center px-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-lg w-full text-center">
        <h1 className="text-xl font-bold text-fp-navy-900 mb-2">
          Something went wrong
        </h1>
        <p className="text-slate-500 text-sm mb-6">
          Please refresh the page. If the problem continues, contact
          support@fairpath.app
        </p>
        <a
          href="/"
          className="inline-flex items-center gap-2 bg-fp-emerald-500 hover:bg-fp-emerald-600 text-white font-bold py-3 px-6 rounded-lg shadow-sm transition-colors"
        >
          <Home className="w-4 h-4" />
          Go Home
        </a>
      </div>
    </div>
  );
}

/** Wizard-specific error element — replaces React Router's "Hey developer 👋" page. */
function WizardErrorElement() {
  const error = useRouteError();

  // Stale-deploy chunk error → silent auto-reload (one attempt)
  if (isChunkLoadError(error)) {
    if (tryChunkReload()) {
      return <div />;
    }
    // Already reloaded — show the "new version" UI (same as RootErrorElement).
    // Info level: expected post-deploy, not actionable.
    captureException(error, (scope) => {
      scope.setLevel("info");
      scope.setTag("error.type", "chunk_load");
      scope.setTag("chunk.reload_attempted", "true");
      scope.setTag("error_context", "wizard");
    });
  } else {
    captureException(error);
  }

  return (
    <div className="min-h-screen bg-fp-slate-100 font-inter flex items-center justify-center px-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-fp-card p-8 max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-fp-emerald-500 rounded-2xl mb-5">
          <Shield className="w-7 h-7 text-white" strokeWidth={2} />
        </div>
        <h1 className="text-xl font-bold text-fp-navy-900 mb-2">
          Something went wrong
        </h1>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed">
          We hit an unexpected error. Your progress has been saved.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              window.location.reload();
            }}
            className="inline-flex items-center gap-2 bg-fp-emerald-500 hover:bg-fp-emerald-600 text-white font-bold py-3 px-6 rounded-fp shadow-sm transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Try Again
          </button>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-fp-navy-900 font-medium py-3 px-6 rounded-fp border border-slate-200 transition-colors"
          >
            <Home className="w-4 h-4" />
            Go Home
          </a>
        </div>
        <p className="text-xs text-slate-400 mt-6">
          If this keeps happening, contact support@fairpath.app
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layouts (always eager — lightweight shells)
// ---------------------------------------------------------------------------

import { RootLayout } from "./layouts/RootLayout";
import { MarketingLayout } from "./layouts/MarketingLayout";
import { BareLayout } from "./layouts/BareLayout";
import { CommercialLayout } from "./layouts/CommercialLayout";
import { ChunkErrorBoundary } from "../components/ChunkErrorBoundary";

// ---------------------------------------------------------------------------
// Utility wrappers (always eager — thin components)
// ---------------------------------------------------------------------------

import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { CREProtectedRoute } from "./components/commercial/CREProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { CompactTrustFooter } from "./components/CompactTrustFooter";

// ---------------------------------------------------------------------------
// Wrappers (eager import of barrel — the wrappers themselves are tiny;
// their internal page component imports are lazy)
// ---------------------------------------------------------------------------

import {
  HomeWrapper,
  AboutPageWrapper,
  FaqPageWrapper,
  BlogPageWrapper,
  BlogPostPageWrapper,
  SuburbLandingPageWrapper,
  NeighborhoodLandingWrapper,
  KatyTXLandingWrapper,
  SugarLandTXLandingWrapper,
  TheWoodlandsTXLandingWrapper,
  PearlandTXLandingWrapper,
  CypressTXLandingWrapper,
  ConroeTXLandingWrapper,
  SpringTXLandingWrapper,
  MissouriCityTXLandingWrapper,
  StaffordTXLandingWrapper,
  FriendswoodTXLandingWrapper,
  LeagueCityTXLandingWrapper,
  ClearLakeTXLandingWrapper,
  PlanoTXLandingWrapper,
  FriscoTXLandingWrapper,
  McKinneyTXLandingWrapper,
  AllenTXLandingWrapper,
  FlowerMoundTXLandingWrapper,
  DentonTXLandingWrapper,
  ProsperTXLandingWrapper,
  SouthlakeTXLandingWrapper,
  TrophyClubTXLandingWrapper,
  ArgyleTXLandingWrapper,
  HighlandVillageTXLandingWrapper,
  DecisionDeciderWrapper,
  QRLandingWrapper,
  QRAdminWrapper,
  AuthCallbackWrapper,
  ContactPageWrapper,
  HowItWorksPageWrapper,
  TermsPageWrapper,
  PrivacyPolicyPageWrapper,
  UPLDisclaimerPageWrapper,
  NotFoundPageWrapper,
  ServerErrorPageWrapper,
  PasswordResetExpiredWrapper,
  AppealHQWrapper,
  FilingGuideWrapper,
  HearingPrepGuideWrapper,
  AccountSettingsPageWrapper,
  CaseWorkspacePageWrapper,
  HelpCenterPageWrapper,
  BillingPageWrapper,
  OutcomeSurveyWrapper,
  HearingModeWrapper,
  LoadingStatesGalleryWrapper,
  ToastDemoPageWrapper,
  FormDemoPageWrapper,
  ShellDemoWrapper,
  VsOwnwellPageWrapper,
  PropertyTaxArgumentLibraryWrapper,
  PropertyTaxArgumentEntryWrapper,
} from "./wrappers";

// ---------------------------------------------------------------------------
// Lazy page components — each import becomes a separate chunk
// ---------------------------------------------------------------------------

// Auth pages
const LoginPage = lazy(() =>
  import("./components/auth/LoginPage").then((m) => ({
    default: m.LoginPage,
  }))
);
const SignupPage = lazy(() =>
  import("./components/auth/SignupPage").then((m) => ({
    default: m.SignupPage,
  }))
);
const ForgotPasswordPage = lazy(() =>
  import("./components/auth/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("./components/auth/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const OAuthConsentPage = lazy(() =>
  import("./components/auth/OAuthConsentPage").then((m) => ({
    default: m.OAuthConsentPage,
  }))
);
const ClaudeOAuthConsentPage = lazy(() =>
  import("./components/auth/ClaudeOAuthConsentPage").then((m) => ({
    default: m.ClaudeOAuthConsentPage,
  })),
);
const ClaudeOAuthLoginPage = lazy(() =>
  import("./components/auth/ClaudeOAuthLoginPage").then((m) => ({
    default: m.ClaudeOAuthLoginPage,
  })),
);

// Marketing Layout pages
const PricingPage = lazy(() =>
  import("./components/PricingPage").then((m) => ({
    default: m.PricingPage,
  }))
);
const CoveragePage = lazy(() =>
  import("./components/CoveragePage").then((m) => ({
    default: m.CoveragePage,
  }))
);
const MethodologyPage = lazy(() =>
  import("./components/MethodologyPage").then((m) => ({
    default: m.MethodologyPage,
  }))
);
const PropertyTaxAppealSoftwarePage = lazy(() =>
  import("./components/PropertyTaxAppealSoftwarePage").then((m) => ({
    default: m.PropertyTaxAppealSoftwarePage,
  }))
);
const AgentTermsPage = lazy(() =>
  import("./components/AgentTermsPage").then((m) => ({
    default: m.AgentTermsPage,
  })),
);
const GuaranteePage = lazy(() =>
  import("./components/GuaranteePage").then((m) => ({
    default: m.GuaranteePage,
  }))
);
const CookCountyAppealGuidePage = lazy(() =>
  import("./pages/CookCountyAppealGuidePage").then((m) => ({
    default: m.CookCountyAppealGuidePage,
  }))
);
const PropertyLookup = lazy(() =>
  import("./components/PropertyLookup").then((m) => ({
    default: m.PropertyLookup,
  }))
);
const PropertyAnalysisDashboard = lazy(() =>
  import("./components/PropertyAnalysisDashboard").then((m) => ({
    default: m.PropertyAnalysisDashboard,
  }))
);
const SamplePacketViewerPage = lazy(() =>
  import("./components/packet/SamplePacketViewerPage").then((m) => ({
    default: m.SamplePacketViewerPage,
  }))
);
const WizardV2PreviewPage = lazy(() =>
  import("./components/wizard/WizardV2PreviewPage").then((m) => ({
    default: m.WizardV2PreviewPage,
  }))
);
const ComparableSalesAnalysis = lazy(() =>
  import("./components/ComparableSalesAnalysis").then((m) => ({
    default: m.ComparableSalesAnalysis,
  }))
);
const ConditionGapEvidence = lazy(() =>
  import("./components/ConditionGapEvidence").then((m) => ({
    default: m.ConditionGapEvidence,
  }))
);
const ExemptionChecker = lazy(() =>
  import("./components/ExemptionChecker").then((m) => ({
    default: m.ExemptionChecker,
  }))
);

// Protected pages
const EvidenceWizard = lazy(() =>
  import("./components/EvidenceWizard").then((m) => ({
    default: m.EvidenceWizard,
  }))
);
const ManualEntryPath = lazy(() =>
  import("./components/ManualEntryPath").then((m) => ({
    default: m.ManualEntryPath,
  }))
);
const CheckoutPage = lazy(() =>
  import("./components/CheckoutPage").then((m) => ({
    default: m.CheckoutPage,
  }))
);
const HearingNavigator = lazy(() =>
  import("./components/HearingNavigator").then((m) => ({
    default: m.HearingNavigator,
  }))
);
const BidHubConsentDossierPage = lazy(() =>
  import("./components/appeal-hq/BidHubConsentDossierPage").then((m) => ({
    default: m.BidHubConsentDossierPage,
  }))
);
const ContractorVerifyPage = lazy(() =>
  import("./components/contractor/ContractorVerifyPage").then((m) => ({
    default: m.ContractorVerifyPage,
  }))
);

const ContractorOptOutPage = lazy(() =>
  import("./components/contractor/ContractorOptOutPage").then((m) => ({
    default: m.ContractorOptOutPage,
  }))
);

const ShieldLandingPage = lazy(() =>
  import("./components/ShieldLandingPage").then((m) => ({
    default: m.ShieldLandingPage,
  }))
);

// Guest pages (no auth required)
const GuestPacketPage = lazy(() =>
  import("./components/GuestPacketPage").then((m) => ({
    default: m.GuestPacketPage,
  }))
);

// Bare Layout pages
const AdminDashboard = lazy(() =>
  import("./components/AdminDashboard").then((m) => ({
    default: m.AdminDashboard,
  }))
);
const AdminOutcomes = lazy(() =>
  import("./components/AdminOutcomes").then((m) => ({
    default: m.AdminOutcomes,
  }))
);
const ContractorOutreachCanary = lazy(() =>
  import("./components/admin/ContractorOutreachCanary").then((m) => ({
    default: m.ContractorOutreachCanary,
  })),
);
const GlassBoxPage = lazy(() =>
  import("./components/GlassBoxPage").then((m) => ({
    default: m.GlassBoxPage,
  }))
);
const PacketDownloadCenter = lazy(() =>
  import("./components/PacketDownloadCenter").then((m) => ({
    default: m.PacketDownloadCenter,
  }))
);
const EmailUnsubscribePage = lazy(() =>
  import("./components/EmailUnsubscribePage").then((m) => ({
    default: m.EmailUnsubscribePage,
  }))
);
const TokenOutcomeSurvey = lazy(() =>
  import("./components/TokenOutcomeSurvey").then((m) => ({
    default: m.TokenOutcomeSurvey,
  }))
);
const VsAppealdeskPage = lazy(() =>
  import("./vs-appealdesk/page").then((m) => ({
    default: m.VsAppealdeskPage,
  }))
);
const VsOconnorPage = lazy(() =>
  import("./vs-oconnor/page").then((m) => ({
    default: m.VsOconnorPage,
  }))
);
const AssessmentNoticesPage = lazy(() =>
  import("./assessment-notices/page").then((m) => ({
    default: m.AssessmentNoticesPage,
  }))
);
const CountySeoPage = lazy(() =>
  import("./components/CountySeoPage").then((m) => ({
    default: m.CountySeoPage,
  }))
);
const PropertyTaxesByStatePage = lazy(() => import("./components/PropertyTaxesByStatePage").then(m => ({ default: m.PropertyTaxesByStatePage })));

const EquityGroundIndexPage = lazy(() =>
  import("./components/EquityGroundIndexPage").then((m) => ({
    default: m.EquityGroundIndexPage,
  })),
);
const StateEquityGroundPage = lazy(() =>
  import("./components/StateEquityGroundPage").then((m) => ({
    default: m.StateEquityGroundPage,
  })),
);
const StateSeoHubPage = lazy(() =>
  import("./components/StateSeoHubPage").then((m) => ({
    default: m.StateSeoHubPage,
  }))
);

const MaintenancePage = lazy(() =>
  import("./components/MaintenancePage").then((m) => ({
    default: m.MaintenancePage,
  }))
);
const QRLanding = lazy(() =>
  import("./components/QRLanding").then((m) => ({
    default: m.QRLanding,
  }))
);

function withCompactTrustFooter(element: ReactNode) {
  return (
    <>
      {element}
      <CompactTrustFooter />
    </>
  );
}

// Dev routes
// AnnualTaxSummary is a PDF-layout design mock built entirely from invented
// sample data (see its DemoSampleBanner), so it belongs with the other /dev
// design references rather than on a customer-reachable path.
const AnnualTaxSummary = lazy(() =>
  import("./components/AnnualTaxSummary").then((m) => ({
    default: m.AnnualTaxSummary,
  }))
);
const DevIndexPage = lazy(() =>
  import("./pages/DevIndexPage").then((m) => ({
    default: m.DevIndexPage,
  }))
);
const AnalysisCtaExperimentPage = lazy(() =>
  import("./pages/AnalysisCtaExperimentPage").then((m) => ({
    default: m.AnalysisCtaExperimentPage,
  }))
);
const PacketPreviewContractPage = lazy(() =>
  import("./pages/PacketPreviewContractPage").then((m) => ({
    default: m.PacketPreviewContractPage,
  }))
);
const CheckResultEvidencePreviewPage = lazy(() =>
  import("./pages/CheckResultEvidencePreviewPage").then((m) => ({
    default: m.CheckResultEvidencePreviewPage,
  }))
);
const ReviewApproveStepPreviewPage = lazy(() =>
  import("./pages/ReviewApproveStepPreviewPage").then((m) => ({
    default: m.ReviewApproveStepPreviewPage,
  }))
);
const CaseWorkspacePreviewPage = lazy(() =>
  import("./pages/CaseWorkspacePreviewPage").then((m) => ({
    default: m.CaseWorkspacePreviewPage,
  }))
);
const EvidencePreviewTimingExperimentPage = lazy(() =>
  import("./pages/EvidencePreviewTimingExperimentPage").then((m) => ({
    default: m.EvidencePreviewTimingExperimentPage,
  }))
);
const EvidenceToggleCards = lazy(() =>
  import("./components/EvidenceToggleCards").then((m) => ({
    default: m.EvidenceToggleCards,
  }))
);
const EditorialShellGalleryPage = lazy(() =>
  import("./pages/EditorialShellGalleryPage").then((m) => ({
    default: m.EditorialShellGalleryPage,
  }))
);
const PhantomFeeCalculator = lazy(() =>
  import("./components/PhantomFeeCalculator").then((m) => ({
    default: m.PhantomFeeCalculator,
  }))
);
const NeighborhoodRealityPanel = lazy(() =>
  import("./components/NeighborhoodRealityPanel").then((m) => ({
    default: m.NeighborhoodRealityPanel,
  }))
);
const MobileEvidenceWizard = lazy(() =>
  import("./components/MobileEvidenceWizard").then((m) => ({
    default: m.MobileEvidenceWizard,
  }))
);
const CommercialPropertyDashboard = lazy(() =>
  import("./components/CommercialPropertyDashboard").then((m) => ({
    default: m.CommercialPropertyDashboard,
  }))
);
const OutcomeTrainerDashboard = lazy(() =>
  import("./components/OutcomeTrainerDashboard").then((m) => ({
    default: m.OutcomeTrainerDashboard,
  }))
);
const RepairCostPrototype = lazy(() =>
  import("./components/RepairCostPrototype").then((m) => ({
    default: m.RepairCostPrototype,
  }))
);
const SmartCompSelector = lazy(() =>
  import("./components/SmartCompSelector").then((m) => ({
    default: m.SmartCompSelector,
  }))
);
const PostcardDesign = lazy(() =>
  import("./components/PostcardDesign").then((m) => ({
    default: m.PostcardDesign,
  }))
);
const EmailTemplates = lazy(() =>
  import("./components/EmailTemplates").then((m) => ({
    default: m.EmailTemplates,
  }))
);
const EvidencePacketCover = lazy(() =>
  import("./components/EvidencePacketCover").then((m) => ({
    default: m.EvidencePacketCover,
  }))
);
const ErrorStatesGallery = lazy(() =>
  import("./components/ErrorStatesGallery").then((m) => ({
    default: m.ErrorStatesGallery,
  }))
);
const EvidenceSelectionStep = lazy(() =>
  import("./components/EvidenceSelectionStep").then((m) => ({
    default: m.EvidenceSelectionStep,
  }))
);
const UploadPage = lazy(() =>
  import("./components/UploadPage").then((m) => ({
    default: m.UploadPage,
  }))
);
const PdfTestPage = lazy(() => import("./pages/PdfTestPage"));
const TestEmailTrigger = lazy(() =>
  import("./pages/TestEmailTrigger").then((m) => ({
    default: m.TestEmailTrigger,
  }))
);

const CREDashboard = lazy(() =>
  import("./components/commercial/CREDashboard").then((m) => ({
    default: m.CREDashboard,
  }))
);
const CommercialEvidenceWizard = lazy(() =>
  import("./components/CommercialEvidenceWizard").then((m) => ({
    default: m.CommercialEvidenceWizard,
  }))
);

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RootErrorElement />,
    children: [
      // ── Marketing Layout (external navbar + footer) ──────────────
      {
        element: <MarketingLayout />,
        children: [
          // Public routes — no auth required
          { path: "/", element: <HomeWrapper /> },
          { path: "/property-taxes-by-state", element: <PropertyTaxesByStatePage /> },
          { path: "/pricing", element: <PricingPage /> },
          { path: "/coverage", element: <CoveragePage /> },
          { path: "/methodology", element: <MethodologyPage /> },
          {
            path: "/property-tax-appeal-software",
            element: <PropertyTaxAppealSoftwarePage />,
          },
          { path: "/guarantee", element: <GuaranteePage /> },
          { path: "/agent-terms", element: <AgentTermsPage /> },
          {
            path: "/cook-county-property-tax-appeal",
            element: <CookCountyAppealGuidePage />,
          },
          { path: "/search", element: <PropertyLookup /> },
          { path: "/upload", element: <UploadPage /> },
          { path: "/property/:qrCode", element: <PropertyQrRedirect /> },
          // /dashboard moved to BareLayout — it has its own header with logo, search, and CTAs
          { path: "/packet/sample", element: <SamplePacketViewerPage /> },
          { path: "/sample-packet", element: <Navigate to="/packet/sample" replace /> },
          { path: "/packet/download", element: <Navigate to="/download-center" replace /> },
          { path: "/wizard/preview", element: <WizardV2PreviewPage /> },
          { path: "/comps", element: <ComparableSalesAnalysis /> },
          { path: "/condition", element: <ConditionGapEvidence /> },
          { path: "/exemptions", element: <ExemptionChecker assessedValue={0} /> },

          // Wizard — public entry; checkout supports either guest delivery or
          // an authenticated account.
          {
            path: "/wizard",
            errorElement: <WizardErrorElement />,
            element: (
              <ErrorBoundary message="Something went wrong. Your progress has been saved.">
                <ChunkErrorBoundary>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><span className="text-fp-slate-400 text-base">Loading...</span></div>}>
                    <EvidenceWizard />
                  </Suspense>
                </ChunkErrorBoundary>
              </ErrorBoundary>
            ),
          },
          {
            path: "/wizard/manual-entry",
            errorElement: <WizardErrorElement />,
            element: (
              <ErrorBoundary message="Something went wrong. Your progress has been saved.">
                <ChunkErrorBoundary>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><span className="text-fp-slate-400 text-base">Loading...</span></div>}>
                    <ManualEntryRoute />
                  </Suspense>
                </ChunkErrorBoundary>
              </ErrorBoundary>
            ),
          },
          {
            path: "/wizard/:id",
            errorElement: <WizardErrorElement />,
            element: (
              <ErrorBoundary message="Something went wrong. Your progress has been saved.">
                <ChunkErrorBoundary>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><span className="text-fp-slate-400 text-base">Loading...</span></div>}>
                    <EvidenceWizard />
                  </Suspense>
                </ChunkErrorBoundary>
              </ErrorBoundary>
            ),
          },

          // Protected routes — require authentication
          {
            path: "/checkout",
            element: (
              <ErrorBoundary message="Something went wrong with checkout. Your card has not been charged.">
                <CheckoutPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "/guest/packet",
            element: (
              <ErrorBoundary message="Something went wrong loading your packet. Please try refreshing, or contact support@fairpath.app.">
                <GuestPacketPage />
              </ErrorBoundary>
            ),
          },
          // FAI-147: retired prototypes; old links land on /dashboard.
          ...RETIRED_ROUTE_REDIRECTS,
          {
            path: "/hearing",
            element: (
              <ProtectedRoute>
                <HearingNavigator />
              </ProtectedRoute>
            ),
          },
          // Public contractor signing surface (BidHub V1.1) — tokenized,
          // no account, noindex. Resolution never consumes the token.
          {
            path: "/contractor/verify/:token",
            element: (
              <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><span className="text-fp-slate-400 text-base">Loading...</span></div>}>
                <ContractorVerifyPage />
              </Suspense>
            ),
          },

          {
            path: "/contractor/opt-out/:token",
            element: (
              <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><span className="text-fp-slate-400 text-base">Loading...</span></div>}>
                <ContractorOptOutPage />
              </Suspense>
            ),
          },

          { path: "/shield", element: <ShieldLandingPage /> },
          {
            path: "/shield/dashboard",
            element: (
              <ProtectedRoute>
                <Navigate to="/appeal-hq" replace />
              </ProtectedRoute>
            ),
          },
          // The design mock that used to render here now lives at
          // /dev/tax-summary. This path stays resolvable because the live
          // `tax_season_summary` email links to it
          // (supabase/functions/_shared/email-templates.ts, "View Full
          // Summary"); it lands on the same case hub as every other Shield
          // CTA in that file rather than on a page of invented figures.
          {
            path: "/tax-summary",
            element: (
              <ProtectedRoute>
                <Navigate to="/appeal-hq" replace />
              </ProtectedRoute>
            ),
          },
        ],
      },

      // ── Legacy commercial service routes ──────────────────────────
      {
        element: <CommercialLayout />,
        children: [
          { path: "/commercial", element: <Navigate to="/pricing" replace /> },
          { path: "/commercial/login", element: <Navigate to="/pricing" replace /> },
          {
            path: "/commercial/dashboard",
            element: (
              <CREProtectedRoute>
                <CREDashboard />
              </CREProtectedRoute>
            ),
          },
          {
            path: "/commercial/wizard",
            errorElement: <WizardErrorElement />,
            element: (
              <ProtectedRoute>
                <div className="min-h-screen bg-fp-slate-100 font-inter flex items-center justify-center px-4">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
                    <h1 className="text-xl font-bold text-fp-navy-900 mb-2">CRE Pro — Coming Soon</h1>
                    <p className="text-slate-500 text-sm">Commercial property tax protest tools are under development. Check back soon.</p>
                  </div>
                </div>
              </ProtectedRoute>
            ),
          },
        ],
      },

      // ── Bare Layout (no external chrome) ─────────────────────────
      {
        element: <BareLayout />,
        children: [
          // UF-04: /dashboard is now a routing gate, not an analysis surface.
          // Redirects legacy URLs to /wizard (with address) or /appeal-hq
          // (authenticated post-purchase hub). PropertyAnalysisDashboard is
          // retained for potential future use but no longer rendered here.
          { path: "/dashboard", element: <DashboardGate /> },

          // Decision Decider — property review surface (standalone, no chrome)
          { path: "/check", element: withCompactTrustFooter(<ErrorBoundary message="Something went wrong loading this property."><CheckGate /></ErrorBoundary>) },

          // Property-code links from already-issued or individually prepared
          // letters. /a/:code is canonical; the aliases remain for recipients
          // who already have a legacy link.
          { path: "/a/:code", element: withCompactTrustFooter(<ErrorBoundary message="Something went wrong loading this property."><QRLandingWrapper /></ErrorBoundary>) },
          { path: "/check/:code", element: withCompactTrustFooter(<ErrorBoundary message="Something went wrong loading this property."><QRLandingWrapper /></ErrorBoundary>) },
          { path: "/qr/:qrCode", element: <PropertyQrRedirect /> },
          { path: "/go/:code", element: withCompactTrustFooter(<QRLanding />) },  // legacy base64 property links

          // Auth pages (public — bare layout, no navbar/footer)
          { path: "/login", element: <LoginPage /> },
          { path: "/signup", element: <SignupPage /> },
          { path: "/auth/callback", element: <AuthCallbackWrapper /> },
          { path: "/oauth/consent", element: <OAuthConsentPage /> },
          { path: "/oauth/claude/login", element: <ClaudeOAuthLoginPage /> },
          { path: "/oauth/claude/consent", element: <ClaudeOAuthConsentPage /> },
          { path: "/forgot-password", element: <ForgotPasswordPage /> },
          { path: "/reset-password", element: <ResetPasswordPage /> },
          {
            path: "/reset-password/expired",
            element: <PasswordResetExpiredWrapper />,
          },

          // Self-rendering pages (own navbar + footer)
          { path: "/about", element: <AboutPageWrapper /> },
          { path: "/faq", element: <FaqPageWrapper /> },
          { path: "/blog", element: <BlogPageWrapper /> },
          { path: "/blog/:slug", element: <BlogPostPageWrapper /> },
          { path: "/suburb/:suburb", element: <SuburbLandingPageWrapper /> },

          // SEO neighborhood landing pages — dynamic, data-driven (20 neighborhoods)
          { path: "/neighborhoods/:slug", element: <NeighborhoodLandingWrapper /> },

          // Property Tax Argument Library — public hub + per-lane pages.
          // The hub renders only published+validated entries via
          // getPublishedArguments(); the per-slug page renders only when
          // getPublishedArgumentBySlug returns an entry, otherwise it
          // redirects back to the hub. Today every seeded entry is draft.
          { path: "/property-tax-arguments", element: <PropertyTaxArgumentLibraryWrapper /> },
          { path: "/property-tax-arguments/:slug", element: <PropertyTaxArgumentEntryWrapper /> },

          // Programmatic county guides. Publication is fail-closed in the
          // county SEO registry; draft or invalid records never enter the
          // sitemap or build-time static HTML output.
          { path: "/property-tax-appeal/equity-ground", element: <EquityGroundIndexPage /> },
          { path: "/property-tax-appeal/:stateSlug/rules/equity-ground", element: <StateEquityGroundPage /> },
          { path: "/property-tax-appeal/:stateSlug", element: <StateSeoHubPage /> },
          { path: "/property-tax-appeal/:stateSlug/:countySlug", element: <CountySeoPage /> },

          // Former top-level copies of six neighborhood pages. vercel.json
          // answers these with a 301; these entries cover in-app navigation.
          { path: "/memorial", element: <Navigate to="/neighborhoods/memorial" replace /> },
          { path: "/river-oaks", element: <Navigate to="/neighborhoods/river-oaks" replace /> },
          { path: "/katy", element: <Navigate to="/neighborhoods/katy" replace /> },
          { path: "/sugar-land", element: <Navigate to="/neighborhoods/sugar-land" replace /> },
          { path: "/energy-corridor", element: <Navigate to="/neighborhoods/energy-corridor" replace /> },
          { path: "/houston", element: <Navigate to="/neighborhoods/houston" replace /> },

          // Suburb SEO landing pages (answer-first, AI/GEO optimized)
          { path: "/katy-tx", element: <KatyTXLandingWrapper /> },
          { path: "/sugar-land-tx", element: <SugarLandTXLandingWrapper /> },
          { path: "/the-woodlands-tx", element: <TheWoodlandsTXLandingWrapper /> },
          { path: "/pearland-tx", element: <PearlandTXLandingWrapper /> },
          { path: "/cypress-tx", element: <CypressTXLandingWrapper /> },
          { path: "/conroe-tx", element: <ConroeTXLandingWrapper /> },
          { path: "/spring-tx", element: <SpringTXLandingWrapper /> },
          { path: "/missouri-city-tx", element: <MissouriCityTXLandingWrapper /> },
          { path: "/stafford-tx", element: <StaffordTXLandingWrapper /> },
          { path: "/friendswood-tx", element: <FriendswoodTXLandingWrapper /> },
          { path: "/league-city-tx", element: <LeagueCityTXLandingWrapper /> },
          { path: "/clear-lake-tx", element: <ClearLakeTXLandingWrapper /> },

          // DFW market suburb SEO landing pages
          { path: "/plano-tx", element: <PlanoTXLandingWrapper /> },
          { path: "/frisco-tx", element: <FriscoTXLandingWrapper /> },
          { path: "/mckinney-tx", element: <McKinneyTXLandingWrapper /> },
          { path: "/allen-tx", element: <AllenTXLandingWrapper /> },
          { path: "/flower-mound-tx", element: <FlowerMoundTXLandingWrapper /> },
          { path: "/denton-tx", element: <DentonTXLandingWrapper /> },
          { path: "/prosper-tx", element: <ProsperTXLandingWrapper /> },
          { path: "/southlake-tx", element: <SouthlakeTXLandingWrapper /> },
          { path: "/trophy-club-tx", element: <TrophyClubTXLandingWrapper /> },
          { path: "/argyle-tx", element: <ArgyleTXLandingWrapper /> },
          { path: "/highland-village-tx", element: <HighlandVillageTXLandingWrapper /> },

          // Redirects: old suburb URLs → new canonical /neighborhoods/:slug
          { path: "/property-tax-protest-katy-tx", element: <Navigate to="/neighborhoods/katy" replace /> },
          { path: "/property-tax-protest-the-woodlands-tx", element: <Navigate to="/neighborhoods/the-woodlands" replace /> },
          { path: "/property-tax-protest-sugar-land-tx", element: <Navigate to="/neighborhoods/sugar-land" replace /> },
          { path: "/property-tax-protest-pearland-tx", element: <Navigate to="/neighborhoods/pearland" replace /> },
          { path: "/property-tax-protest-kingwood-tx", element: <Navigate to="/neighborhoods/kingwood" replace /> },

          { path: "/contact", element: <ContactPageWrapper /> },
          { path: "/how-it-works", element: <HowItWorksPageWrapper /> },

          { path: "/vs-ownwell", element: <VsOwnwellPageWrapper /> },
          { path: "/vs-appealdesk", element: <VsAppealdeskPage /> },
          { path: "/vs-oconnor", element: <VsOconnorPage /> },
          { path: "/assessment-notices", element: <AssessmentNoticesPage /> },
          { path: "/vs-texasprotax", element: <Navigate to="/vs-oconnor" replace /> },

          { path: "/terms", element: <TermsPageWrapper /> },
          { path: "/privacy", element: <PrivacyPolicyPageWrapper /> },
          { path: "/disclaimer", element: <UPLDisclaimerPageWrapper /> },
          { path: "/error", element: <ServerErrorPageWrapper /> },
          { path: "/hearing-prep", element: <HearingPrepGuideWrapper /> },

          // Protected app pages
          { path: "/admin", element: <AdminRoute><AdminDashboard /></AdminRoute> },
          { path: "/admin/qr", element: <AdminRoute><QRAdminWrapper /></AdminRoute> },
          { path: "/admin/outcomes", element: <AdminRoute><AdminOutcomes /></AdminRoute> },
          { path: "/admin/contractor-outreach-canary", element: <AdminRoute><ContractorOutreachCanary /></AdminRoute> },
          {
            path: "/appeal-hq",
            element: (
              <ProtectedRoute>
                <AppErrorBoundary context="AppealHQ">
                  <ChunkErrorBoundary>
                    <AppealHQWrapper />
                  </ChunkErrorBoundary>
                </AppErrorBoundary>
              </ProtectedRoute>
            ),
          },
          { path: "/filing-guide", element: <ProtectedRoute><FilingGuideWrapper /></ProtectedRoute> },
          { path: "/appeal-hq/hearing-navigator", element: <ProtectedRoute><HearingNavigator /></ProtectedRoute> },
          { path: "/appeal-hq/bidhub", element: <ProtectedRoute><BidHubConsentDossierPage /></ProtectedRoute> },
          {
            path: "/cases",
            element: <ProtectedRoute><CaseWorkspacePageWrapper /></ProtectedRoute>,
          },
          {
            path: "/account/settings",
            element: (
              <ProtectedRoute>
                <AccountSettingsPageWrapper />
              </ProtectedRoute>
            ),
          },
          {
            path: "/account/billing",
            element: (
              <ProtectedRoute>
                <BillingPageWrapper />
              </ProtectedRoute>
            ),
          },
          { path: "/help", element: <HelpCenterPageWrapper /> },
          { path: "/outcome-survey", element: <ProtectedRoute><OutcomeSurveyWrapper /></ProtectedRoute> },
          { path: "/glass-box", element: <ProtectedRoute><GlassBoxPage /></ProtectedRoute> },
          {
            path: "/download-center",
            element: <ProtectedRoute><PacketDownloadCenter /></ProtectedRoute>,
          },
          { path: "/outcome/:token", element: <TokenOutcomeSurvey /> },
          { path: "/unsubscribe", element: <EmailUnsubscribePage /> },
          { path: "/maintenance", element: <MaintenancePage /> },

          // Dev routes — only available in development builds
          ...(import.meta.env.DEV
            ? [
                { path: "/dev", element: <DevIndexPage /> },
                // Hearing Mode is a sample view built on placeholder figures. It
                // used to sit at /hearing-mode as a customer-reachable ProtectedRoute;
                // it belongs with the other development-only pages, like /dev/tax-summary.
                { path: "/dev/hearing-mode", element: <HearingModeWrapper /> },
                {
                  path: "/dev/conversion-journey/analysis-cta",
                  element: <AnalysisCtaExperimentPage />,
                },
                {
                  path: "/dev/packet-preview-contract",
                  element: <PacketPreviewContractPage />,
                },
                {
                  path: "/dev/check-result-evidence",
                  element: <CheckResultEvidencePreviewPage />,
                },
                {
                  path: "/dev/review-approve",
                  element: <ReviewApproveStepPreviewPage />,
                },
                ...(import.meta.env.DEV
                  ? [
                      {
                        path: "/dev/case-workspace",
                        element: <CaseWorkspacePreviewPage />,
                      },
                    ]
                  : []),
                {
                  path: "/dev/conversion-journey/evidence-preview",
                  element: <EvidencePreviewTimingExperimentPage />,
                },
                { path: "/dev/cards", element: <EvidenceToggleCards /> },
                { path: "/dev/tax-summary", element: <AnnualTaxSummary /> },
                { path: "/dev/editorial-shell", element: <EditorialShellGalleryPage /> },
                {
                  path: "/dev/calculator",
                  element: <PhantomFeeCalculator />,
                },
                {
                  path: "/dev/reality",
                  element: <NeighborhoodRealityPanel />,
                },
                {
                  path: "/dev/mobile-wizard",
                  element: <MobileEvidenceWizard />,
                },
                {
                  path: "/dev/commercial",
                  element: <CommercialPropertyDashboard />,
                },
                {
                  path: "/dev/outcome-trainer",
                  element: <OutcomeTrainerDashboard />,
                },
                {
                  path: "/dev/repair-cost-prototype",
                  element: <RepairCostPrototype />,
                },
                {
                  path: "/dev/comp-selector",
                  element: <SmartCompSelector />,
                },
                { path: "/dev/postcard", element: <PostcardDesign /> },
                { path: "/dev/emails", element: <EmailTemplates /> },
                {
                  path: "/dev/packet-cover",
                  element: <EvidencePacketCover />,
                },
                {
                  path: "/dev/error-states",
                  element: <ErrorStatesGallery />,
                },
                {
                  path: "/dev/loading-states",
                  element: <LoadingStatesGalleryWrapper />,
                },
                { path: "/dev/toasts", element: <ToastDemoPageWrapper /> },
                { path: "/dev/forms", element: <FormDemoPageWrapper /> },
                {
                  path: "/dev/evidence-selection",
                  element: <EvidenceSelectionStep />,
                },
                { path: "/dev/shell", element: <ShellDemoWrapper /> },
                { path: "/dev/pdf-test", element: <PdfTestPage /> },
                { path: "/dev/test-email", element: <TestEmailTrigger /> },
                {
                  path: "/dev/commercial-wizard",
                  element: (
                    <CommercialEvidenceWizard
                      config={{
                        // Dev fixture — partial Harris County config for testing
                        id: "harris_tx",
                        countyName: "Harris",
                        stateName: "Texas",
                        stateAbbr: "TX",
                        fipsCode: "48201",
                        attomFips: "48201",
                        archetypeId: "tx_protest" as const,
                        isOperational: true,
                        combinedTaxRate: 0.0245,
                        assessmentRatio: 1.0,
                        annualIncreaseCapPct: 10,
                        supportsCommercial: true,
                        commercialDisclosureProtection: true,
                        circuitBreakerCapPct: 20,
                        districtName: "Harris County Appraisal District",
                        noticeMailMonth: 4,
                        protestDeadlineRule: "May 15 or 30 days after notice",
                        promptContext: {
                          filingSteps: "",
                          hearingScript: "",
                          equityArgument: "",
                        },
                      } as unknown as Parameters<typeof CommercialEvidenceWizard>[0]["config"]}
                      countyAssessedValue={2500000}
                      propertyAddress="123 Commercial Dr, Houston, TX 77001"
                    />
                  ),
                },
              ]
            : []),

          // Catch-all 404
          { path: "*", element: <NotFoundPageWrapper /> },
        ],
      },
    ],
  },
]);
